/**
 * Milkath Dashboard — Executive Decision Dashboard.
 *
 * Not an analytics tool — a CEO's daily operating system. Every card is
 * powered by the Business Intelligence Engine. No metric is calculated here.
 *
 * Sections:
 *   1. Business Health hero
 *   2. Today's Priorities (top 5 opportunities)
 *   3. Executive KPIs
 *   4. Revenue Trends (grain-selectable)
 *   5. Product Intelligence (tabs)
 *   6. Category Intelligence
 *   7. Marketplace Intelligence
 *   8. Business Insights
 *   9. Opportunities Center
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Sparkles, TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown,
  Upload, Package, Boxes, Store, Calendar, AlertTriangle, ChevronRight,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import GlobalFilterBar, { useFilters } from "../components/dashboard/GlobalFilterBar";
import GlobalSearch from "../components/dashboard/GlobalSearch";
import OpportunitiesCenter from "../components/dashboard/OpportunitiesCenter";
import ResetWorkspaceButton from "../components/app/ResetWorkspaceButton";
import { formatINR as money, formatNumber as num, formatPct } from "../lib/format";

const pct = (n) => formatPct(n, { asFraction: false, digits: 1 });
const TREND = {
  rapid_growth:  { color: "text-emerald-400", icon: TrendingUp },
  growing:       { color: "text-emerald-400", icon: ArrowUp },
  stable:        { color: "text-zinc-400",   icon: Minus },
  declining:     { color: "text-amber-400",  icon: ArrowDown },
  rapid_decline: { color: "text-red-400",    icon: TrendingDown },
  unknown:       { color: "text-zinc-500",   icon: Minus },
};

// ─── Section 1: Health Hero ───
function HealthHero({ health }) {
  if (!health) return null;
  const s = health.overall_score;
  const tone = s >= 80 ? "text-emerald-400" : s >= 65 ? "text-emerald-300" : s >= 45 ? "text-amber-400" : "text-red-400";
  const label = health.verdict.replace("_", " ");
  return (
    <section className="rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900/80 to-zinc-950 p-6 sm:p-8" data-testid="dash-health">
      <div className="grid md:grid-cols-[auto,1fr] gap-6 items-start">
        <div className="flex md:flex-col items-center md:items-start gap-4">
          <div className={`text-6xl sm:text-7xl font-semibold tracking-tight ${tone}`} style={{ fontFamily: "var(--font-heading)" }} data-testid="dash-health-score">
            {s}<span className="text-zinc-600 text-2xl">/100</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className={`h-4 w-4 ${tone}`} />
            <p className={`text-xs uppercase tracking-[0.18em] font-bold ${tone}`} data-testid="dash-health-verdict">{label}</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80">Business Health</p>
          <h2 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
            Here's where your business stands
          </h2>

          {health.negative_factors?.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2">What's holding you back</p>
              <ul className="space-y-1">
                {health.negative_factors.slice(0, 3).map((f) => (
                  <li key={f.label} className="text-sm text-zinc-300 flex items-start gap-2">
                    <AlertTriangle className="h-3 w-3 text-red-400 mt-1 shrink-0" />
                    <span>{f.label} — <span className="text-zinc-500">{f.score.toFixed(1)}/{f.max}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {health.suggestions?.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2">How to improve</p>
              <ul className="space-y-1.5" data-testid="dash-health-suggestions">
                {health.suggestions.slice(0, 4).map((s2, i) => (
                  <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                    <Sparkles className="h-3 w-3 text-orange-400 mt-1 shrink-0" />
                    <span>{s2}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Section 2: Today's Priorities ───
const PRIORITY_TONE = { high: "border-red-500/40 bg-red-500/5", medium: "border-amber-500/40 bg-amber-500/5", low: "border-zinc-700 bg-zinc-900/40" };
const PRIORITY_DOT = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-zinc-500" };
function Priorities({ opportunities }) {
  const top = opportunities.filter((o) => o.status === "open").slice(0, 4);
  if (top.length === 0) return null;
  return (
    <section data-testid="dash-priorities">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500">Today's priorities</h2>
        <Link to="/opportunities" className="text-xs text-orange-500 hover:text-orange-400 inline-flex items-center gap-1" data-testid="dash-priorities-see-all">
          See all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {top.map((o) => (
          <div key={o.opportunity_id} className={`rounded-xl border p-4 ${PRIORITY_TONE[o.priority] || PRIORITY_TONE.low}`} data-testid={`priority-${o.opportunity_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[o.priority] || PRIORITY_DOT.low}`} />
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-400">{o.priority}</span>
              <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">· {o.confidence}%</span>
              {o.estimated_revenue_impact > 0 && (
                <span className="ml-auto text-xs text-zinc-100 tabular-nums font-medium">{money(o.estimated_revenue_impact)}</span>
              )}
            </div>
            <p className="mt-1.5 text-sm font-medium text-zinc-100">{o.title}</p>
            <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{o.description}</p>
            <p className="mt-2 text-xs text-zinc-300"><span className="text-zinc-500">Action:</span> {o.recommended_action}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Section 3: Executive KPIs ───
function KPI({ label, value, sub, delta, testid }) {
  const deltaTone = delta == null ? "" : delta >= 0 ? "text-emerald-400" : "text-red-400";
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 transition-colors hover:border-zinc-700" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-zinc-50 tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500 truncate">{sub}</p>}
      {delta != null && <p className={`mt-1 text-xs ${deltaTone}`}>{pct(delta)}</p>}
    </div>
  );
}

// ─── Section 4: Revenue Trends ───
function TrendChart({ series }) {
  if (!series?.length) {
    return <p className="text-sm text-zinc-500" data-testid="trend-chart">Not enough data yet.</p>;
  }
  const max = Math.max(...series.map((s) => s.revenue || 0), 1);
  return (
    <div className="flex items-end gap-[3px] h-40" data-testid="trend-chart">
      {series.slice(-60).map((s, i) => {
        const h = Math.max(2, Math.round(((s.revenue || 0) / max) * 100));
        return (
          <div key={s.period + i} title={`${s.period}: ${money(s.revenue)}`} className="flex-1 bg-orange-500/60 hover:bg-orange-500 rounded-sm transition-colors" style={{ height: `${h}%` }} />
        );
      })}
    </div>
  );
}

// ─── Section 5: Product Intelligence ───
function ProductTabs({ products }) {
  const TABS = [
    ["top_sellers", "Top sellers"],
    ["rapid_growth", "Growing"],
    ["rapid_decline", "Declining"],
    ["critical", "Critical"],
    ["high_return", "High return"],
    ["high_cancellation", "High cancel"],
  ];
  const [active, setActive] = useState(TABS[0][0]);
  const rows = (products?.[active] || []).slice(0, 6);
  return (
    <section data-testid="dash-products">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 shrink-0"><Package className="h-3.5 w-3.5" /> Product intelligence</h2>
        <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1 text-xs overflow-x-auto min-w-0 max-w-full">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setActive(k)} data-testid={`prod-tab-${k}`}
              className={`px-2 py-1 rounded whitespace-nowrap ${active === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-500">No products in this category.</div>
      ) : (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 divide-y divide-zinc-800/60">
          {rows.map((p) => {
            const t = TREND[p.trend] || TREND.unknown;
            const Icon = t.icon;
            return (
              <div key={p.product_id} className="p-4 flex items-center gap-4" data-testid={`prod-${p.product_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-100 truncate">{p.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{p.sku || "no SKU"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm text-zinc-100 tabular-nums">{money(p.total_revenue)}</p>
                  <p className="text-xs text-zinc-500">{p.contribution_pct.toFixed(1)}% share</p>
                </div>
                <div className={`text-xs ${t.color} min-w-[80px] text-right`}>
                  <Icon className="inline h-3 w-3" /> {pct(p.revenue_change_pct)}
                </div>
                <span className={`min-w-[46px] text-center text-xs px-2 py-0.5 rounded-md font-medium ${
                  p.health_bucket === "excellent" ? "bg-emerald-500/15 text-emerald-300" :
                  p.health_bucket === "healthy"   ? "bg-emerald-500/10 text-emerald-300/80" :
                  p.health_bucket === "needs_attention" ? "bg-amber-500/15 text-amber-300" :
                                                    "bg-red-500/15 text-red-300"
                }`}>{p.health_score}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── Section 6/7: Category + Marketplace grids ───
function DimensionCard({ title, icon: Icon, rows, valueKey = "revenue", labelKey, changeKey = "revenue_change_pct", testid }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid={testid}>
      <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3 flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {title}</h3>
      {(rows || []).slice(0, 6).map((r) => {
        const t = TREND[r.trend] || TREND.unknown;
        return (
          <div key={r[labelKey]} className="flex items-center justify-between py-2 hover:bg-zinc-800/30 rounded px-1 transition-colors">
            <span className="text-sm text-zinc-200 truncate flex-1 mr-3">{r[labelKey]}</span>
            <span className="text-sm text-zinc-300 tabular-nums">{money(r[valueKey])}</span>
            <span className={`text-xs ml-3 min-w-[60px] text-right ${t.color}`}>{pct(r[changeKey])}</span>
          </div>
        );
      })}
      {(rows || []).length === 0 && <p className="text-sm text-zinc-500">Not enough data.</p>}
    </div>
  );
}

// ─── Section 8: Insights ───
function InsightGrid({ insights }) {
  if (!insights?.length) return null;
  const border = { opportunity: "border-emerald-500/40 bg-emerald-500/5", info: "border-zinc-700 bg-zinc-900/30", warning: "border-amber-500/40 bg-amber-500/5", risk: "border-red-500/40 bg-red-500/5" };
  return (
    <section data-testid="dash-insights">
      <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Business insights</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {insights.slice(0, 6).map((i) => (
          <div key={i.insight_id} className={`rounded-xl border p-4 ${border[i.severity] || border.info}`} data-testid={`insight-${i.insight_id}`}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-400">{i.priority}</span>
              <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">· {Math.round(i.confidence * 100)}%</span>
            </div>
            <p className="mt-1.5 text-sm font-medium text-zinc-100">{i.title}</p>
            <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{i.description}</p>
            <p className="mt-2 text-xs text-zinc-300"><span className="text-zinc-500">Action:</span> {i.recommended_action}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Empty State ───
function EmptyState({ userName, onReset }) {
  return (
    <div className="pt-8">
      {/* Always allow reset from Dashboard — the POST is idempotent, so it's
          safe when data drifted outside the current filter window OR when the
          workspace is already empty. Users no longer need to visit Settings. */}
      <div className="flex justify-end mb-6">
        <ResetWorkspaceButton onReset={onReset} testid="dash-reset-workspace" />
      </div>
      <div className="max-w-3xl mx-auto py-16 text-center" data-testid="dash-empty">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-orange-600/10 border border-orange-600/30 flex items-center justify-center">
          <Upload className="h-6 w-6 text-orange-500" />
        </div>
        <h1 className="mt-6 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
          Welcome{userName ? `, ${userName}` : ""}.
        </h1>
        <p className="mt-3 text-zinc-400 max-w-xl mx-auto">
          Upload your order history — Milkath will automatically surface today&rsquo;s top revenue opportunities. Most merchants finish their first import in under a minute.
        </p>
        <Link to="/imports" data-testid="dash-empty-cta" className="mt-8 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
          <Upload className="h-4 w-4" /> Import your first CSV
        </Link>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───
export default function Dashboard() {
  const { user } = useAuth();
  const { state: filters, setState: setFilters, query } = useFilters();
  const [data, setData] = useState({ overview: null, products: null, categories: null, marketplaces: null, trends: null, insights: null, opportunities: null });
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [grain, setGrain] = useState("day");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = query ? `?${query}` : "";
      const [ov, p, c, m, t, i, o] = await Promise.all([
        api.get(`/business-intelligence/overview${qp}`),
        api.get(`/business-intelligence/products${qp}`),
        api.get(`/business-intelligence/categories${qp}`),
        api.get(`/business-intelligence/marketplaces${qp}`),
        api.get(`/business-intelligence/trends${qp ? qp + "&" : "?"}grain=${grain}&days_back=90`),
        api.get(`/business-intelligence/insights${qp}`),
        api.get(`/opportunities`),
      ]);
      setData({ overview: ov.data, products: p.data, categories: c.data, marketplaces: m.data, trends: t.data, insights: i.data, opportunities: o.data });
    } finally { setLoading(false); }
  }, [query, grain]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault(); setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const kpis = data.overview?.kpis;
  const bench = data.overview?.benchmarks;
  const empty = !loading && (!kpis || kpis.orders === 0);
  const marketplaces = useMemo(() => (data.marketplaces?.rows || []).map((r) => r.marketplace), [data.marketplaces]);
  const categoryList = useMemo(() => (data.categories?.rows || []).map((r) => r.category), [data.categories]);

  if (loading && !data.overview) {
    return <div className="text-sm text-zinc-500 max-w-7xl mx-auto p-6" data-testid="dash-loading">Preparing your daily briefing…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
      <GlobalFilterBar state={filters} setState={setFilters} marketplaces={marketplaces} categories={categoryList} onOpenSearch={() => setSearchOpen(true)} />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      {empty ? (
        <EmptyState userName={user?.name?.split(" ")[0]} onReset={load} />
      ) : (
      <div className="pt-8 space-y-12">
        {/* Greeting */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Today</p>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
              {greetingFor(user?.name)}
            </h1>
          </div>
          <ResetWorkspaceButton onReset={load} testid="dash-reset-workspace" />
        </div>

        {/* 1. Business Health */}
        <HealthHero health={data.overview?.health} />

        {/* 2. Today's Priorities */}
        <Priorities opportunities={data.opportunities || []} />

        {/* 3. Executive KPIs */}
        <section data-testid="dash-kpis">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Executive KPIs</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KPI label="Revenue" value={money(kpis.gross_revenue)} testid="kpi-revenue" delta={bench?.last_month?.revenue?.change_pct} />
            <KPI label="Orders"  value={num(kpis.orders)}          testid="kpi-orders"  delta={bench?.last_month?.orders?.change_pct} />
            <KPI label="AOV"     value={money(kpis.aov)}           testid="kpi-aov"     delta={bench?.last_month?.aov?.change_pct} />
            <KPI label="Units sold"    value={num(kpis.units_sold)}         testid="kpi-units" />
            <KPI label="Products"      value={num(kpis.unique_products)}    testid="kpi-products" />
            <KPI label="Marketplaces"  value={num(kpis.unique_marketplaces)} testid="kpi-marketplaces" />
            <KPI label="Return rate"        value={`${(data.overview?.order_distribution?.rates?.return * 100 || 0).toFixed(1)}%`}       testid="kpi-return-rate" />
            <KPI label="Cancellation rate"  value={`${(data.overview?.order_distribution?.rates?.cancellation * 100 || 0).toFixed(1)}%`} testid="kpi-cancel-rate" />
            <KPI label="Categories"    value={num(kpis.unique_categories)}  testid="kpi-categories" />
            <KPI label="Net revenue"   value={money(kpis.net_revenue)}      testid="kpi-net-revenue" />
          </div>
        </section>

        {/* 4. Revenue Trends */}
        <section data-testid="dash-trends">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 shrink-0"><Calendar className="h-3.5 w-3.5" /> Revenue trends</h2>
            <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1 text-xs overflow-x-auto min-w-0 max-w-full">
              {["day", "week", "month", "quarter", "year"].map((g) => (
                <button key={g} onClick={() => setGrain(g)} data-testid={`trend-grain-${g}`}
                  className={`px-2 py-1 rounded capitalize ${grain === g ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <TrendChart series={data.trends?.series?.series || []} />
          </div>
        </section>

        {/* 5. Product Intelligence */}
        <ProductTabs products={data.products} />

        {/* 6 + 7. Categories + Marketplaces */}
        <section className="grid lg:grid-cols-2 gap-6">
          <DimensionCard title="Category intelligence" icon={Boxes}    rows={data.categories?.rows}    labelKey="category"    testid="dash-categories" />
          <DimensionCard title="Marketplace intelligence" icon={Store} rows={data.marketplaces?.rows} labelKey="marketplace" testid="dash-marketplaces" />
        </section>

        {/* 8. Insights */}
        <InsightGrid insights={data.insights || []} />

        {/* 9. Opportunities Center */}
        <OpportunitiesCenter opportunities={data.opportunities || []} onChange={load} />
      </div>
      )}
    </div>
  );
}

function greetingFor(name) {
  const first = (name || "").split(" ")[0] || "there";
  const hour = new Date().getHours();
  const g = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return `${g}, ${first}.`;
}
